/**
 * Answer mode's state machine (lesson library PRD §4.4), kept out of the
 * `.tsx` so it runs under `node --test`. It decides; the view performs the
 * effects. It never judges an answer: the speaker's own reply in the
 * recording is the feedback, so nothing here says right or wrong.
 */

/** With no press this long after the stop, the recording goes on and the cue is unanswered. */
export const TIMEOUT_MS = 6_000;

type Base = {
  /** Where each question ends, in the recording's ms. */
  cues: readonly number[];
  /**
   * How many cues have been paused at. Cues are in order and a seek back
   * lands before `next`, so a cue that has paused once never pauses again.
   */
  next: number;
  /** The recording ended while a cue was still waiting: nothing to resume. */
  over: boolean;
  /**
   * The tab was hidden while the recording was playing; coming back resumes
   * it. Only that: a story nobody started, or one the learner paused, stays
   * as it was when the tab returns.
   */
  away: boolean;
};

export type State = Base &
  // Before the first play, and after the learner paused: nothing is running.
  (
    | { kind: 'idle' }
    | { kind: 'playing' }
    // `stoppedAt` is null while the tab is hidden: the timer is not running.
    | { kind: 'waiting'; cueIndex: number; stoppedAt: number | null }
    | { kind: 'holding'; cueIndex: number; responseMs: number }
    | { kind: 'done' }
  );

export type Event =
  // The learner's own play and pause, so the machine knows what was running.
  | { kind: 'play' }
  | { kind: 'pause' }
  | { kind: 'tick'; posMs: number; nowMs: number }
  | { kind: 'press'; nowMs: number }
  | { kind: 'release'; nowMs: number }
  | { kind: 'timeout' }
  | { kind: 'hidden' }
  | { kind: 'visible'; nowMs: number }
  | { kind: 'ended' };

/**
 * `sendAnswer` always comes after `resume` in one release: the recording is
 * audible again before anything is uploaded. `clip` says whether this hold
 * has a recording the view is to hand on, or the cue goes unanswered.
 */
export type Effect =
  | { kind: 'pause' }
  | { kind: 'resume' }
  | { kind: 'startClip'; cueIndex: number }
  | { kind: 'sendAnswer'; cueIndex: number; responseMs: number | null; clip: boolean };

/**
 * A whole number of ms within what the server takes (0 to 6000): the clock
 * the view reads is fractional, which the `Int` argument refuses, and a press
 * can land a few ms before the timer that would have ended the wait.
 */
function responseMs(ms: number): number {
  return Math.min(TIMEOUT_MS, Math.max(0, Math.round(ms)));
}

export function init(cues: readonly number[]): State {
  return { kind: 'idle', cues, next: 0, over: false, away: false };
}

type Result = { state: State; effects: Effect[] };

const stay = (state: State): Result => ({ state, effects: [] });

/** Back to the recording after a cue: resumed, unless it already ran out. */
function resumed(s: State, cueIndex: number, responseMs: number | null, clip: boolean): Result {
  const send: Effect = { kind: 'sendAnswer', cueIndex, responseMs, clip };
  if (s.over) return { state: { ...s, kind: 'done' }, effects: [send] };
  return { state: { ...s, kind: 'playing' }, effects: [{ kind: 'resume' }, send] };
}

export function answerMode(s: State, e: Event): Result {
  switch (e.kind) {
    case 'play':
      if (s.kind !== 'idle') return stay(s);
      return { state: { ...s, kind: 'playing' }, effects: [{ kind: 'resume' }] };
    case 'pause':
      // Also the audio element's own `pause` event, which the browser raises
      // for a screen lock, a call or a headphone button as well as for ours.
      // While `away` the pause is the hidden tab's own, and the way back from
      // it is `visible`; reading it as the learner's would leave the story
      // idle when they return.
      if (s.kind !== 'playing' || s.away) return stay(s);
      return { state: { ...s, kind: 'idle', away: false }, effects: [{ kind: 'pause' }] };
    case 'tick': {
      if (s.kind !== 'playing') return stay(s);
      const at = s.cues[s.next];
      if (at === undefined || e.posMs < at) return stay(s);
      return {
        state: { ...s, kind: 'waiting', cueIndex: s.next, stoppedAt: e.nowMs, next: s.next + 1 },
        effects: [{ kind: 'pause' }],
      };
    }
    case 'press': {
      // A press with no question waiting starts nothing, and a second press
      // in one hold is the same hold.
      if (s.kind !== 'waiting' || s.stoppedAt === null) return stay(s);
      return {
        state: { ...s, kind: 'holding', cueIndex: s.cueIndex, responseMs: responseMs(e.nowMs - s.stoppedAt) },
        effects: [{ kind: 'startClip', cueIndex: s.cueIndex }],
      };
    }
    case 'release':
      if (s.kind !== 'holding') return stay(s);
      return resumed(s, s.cueIndex, s.responseMs, true);
    case 'timeout':
      // Not while held: a long answer is an answer. Not while hidden either,
      // where `stoppedAt` is null and a late-firing timer is stale.
      if (s.kind !== 'waiting' || s.stoppedAt === null) return stay(s);
      return resumed(s, s.cueIndex, null, false);
    case 'hidden':
      if (s.kind === 'playing' && !s.away) return { state: { ...s, away: true }, effects: [{ kind: 'pause' }] };
      if (s.kind === 'waiting') return stay({ ...s, stoppedAt: null });
      return stay(s);
    case 'visible':
      if (s.kind === 'playing' && s.away) return { state: { ...s, away: false }, effects: [{ kind: 'resume' }] };
      if (s.kind === 'waiting' && s.stoppedAt === null) return stay({ ...s, stoppedAt: e.nowMs });
      return stay(s);
    case 'ended':
      if (s.kind === 'playing' || s.kind === 'idle') return stay({ ...s, kind: 'done' });
      if (s.kind === 'waiting' || s.kind === 'holding') return stay({ ...s, over: true });
      return stay(s);
  }
}

/** The middle response; the mean of the two middle ones for an even count. Unanswered cues are not in it. */
export function medianMs(responses: readonly (number | null)[]): number | null {
  const xs = responses.filter((r): r is number => r !== null).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = xs.length >> 1;
  return xs.length % 2 ? (xs[mid] as number) : Math.round(((xs[mid - 1] as number) + (xs[mid] as number)) / 2);
}

export function summary(responses: readonly (number | null)[]): {
  asked: number;
  answered: number;
  medianMs: number | null;
} {
  return {
    asked: responses.length,
    answered: responses.filter((r) => r !== null).length,
    medianMs: medianMs(responses),
  };
}

/** A clip handed on, and what has come back for it. */
export type SentAnswer = {
  cueIndex: number;
  responseMs: number | null;
  clipped: boolean;
  transcript: string | null;
  /** When it was sent, in epoch ms. */
  at: number;
  /** The server refused it: nothing will come back. */
  failed: boolean;
};

/** Transcripts are asked for this long after a clip is sent, then given up on quietly. */
export const POLL_FOR_MS = 60_000;

/**
 * The answers still worth asking about: a clip, not refused, with no words
 * yet and not yet a minute old. Each answer ages on its own, so a later
 * clip never switches an earlier one's transcript off.
 */
export function awaitingTranscript(sent: readonly SentAnswer[], nowMs: number): SentAnswer[] {
  return sent.filter((a) => a.clipped && !a.failed && a.transcript === null && nowMs - a.at < POLL_FOR_MS);
}
